import { memo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  TableSortLabel,
  Tooltip,
  Skeleton,
  Stack,
  Menu,
  MenuItem,
  IconButton,
  Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { visuallyHidden } from "@mui/utils";
import amazonLogo from "../../../assets/svgs/amazon.svg";
import myntraLogo from "../../../assets/svgs/myntra.svg";
import flipkartLogo from "../../../assets/svgs/flipkart.svg";
import adyogiLogo from "../../../assets/images/Adyogi_logoz.svg";
import { numberFormatter } from "../../../helper/Reports/common";
import imageThumbnail from "../../../assets/images/image_thumbnail.svg";
import SwitchBtn from "../../../components/Buttons/switchBtn";
import { isAdStatusEnabled } from "../../../helper/bulkSelection/bulkProductAdUpdate";

const CHECKBOX_COL_WIDTH = 44;
const STATUS_COL_WIDTH = 76;
const SELECTION_DISABLED_TOOLTIP =
  "Group by Child Product (ASIN) to enable or pause ads";
const NAME_WRAP_COLUMN_KEYS = new Set([
  "VariantAnalysisTablePPMPlaceholder.campaignName",
  "VariantAnalysisTablePPMPlaceholder.adGroupName",
  "VariantAnalysisTablePPMPlaceholder.portfolioName",
]);
const nameWrapCellSx = {
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  overflow: "hidden",
};
const nameWrapTextSx = {
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  maxWidth: "100%",
};

function ProductAnalyticsTable({
  dataHeads = [],
  dataBody = [],
  isLoading = true,
  onSortClick,
  orderByData = [],
  summaryData = {},
  showPercentage,
  summaryDataWithoutFilter,
  isComparisonView = false,
  groupByOptionsData = [],
  onExtraGroupByChange = () => {},
  showGroupByPlusIcon = true,
  noDataFoundMessage = "No Data Found",
  showCheckbox = false,
  showAdStatusSwitch = false,
  selectionEnabled = false,
  adStatusSwitchEnabled = false,
  onToggleRow,
  onToggleAll,
  onToggleStatus,
  getRowKey,
  highlightNoDataFound = false,
}) {
  const [orderBy, setOrderBy] = useState(orderByData[0][0] || "");
  const [order, setOrder] = useState(orderByData[0][1] || "desc");
  const [anchorEl, setAnchorEl] = useState(null);
  const leadingOffset =
    (showCheckbox ? CHECKBOX_COL_WIDTH : 0) +
    (showAdStatusSwitch ? STATUS_COL_WIDTH : 0);
  const allSelectableChecked =
    selectionEnabled &&
    dataBody.length > 0 &&
    dataBody.every((row) => row?.isChecked);
  const someSelectableChecked = dataBody.some((row) => row?.isChecked);


  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    onSortClick && onSortClick(property, isAsc ? "desc" : "asc");
  };

  const handlePlusClick = (event, rowIndex = null) => {
    setAnchorEl(event.currentTarget);
  };
  const handleRemoveExtraColumnClick = (key) => {
    const remainingHeads = dataHeads.filter((col) => col.key !== key);
    const stillHasAdGroup = remainingHeads.some(
      (col) => col.key === "VariantAnalysisTablePPMPlaceholder.adGroupName"
    );
    const stillHasCampaign = remainingHeads.some(
      (col) => col.key === "VariantAnalysisTablePPMPlaceholder.campaignName"
    );
    const toRemove = [key];
    if (key === "VariantAnalysisTablePPMPlaceholder.campaignName" && !stillHasAdGroup) {
      toRemove.push("VariantAnalysisTablePPMPlaceholder.campaignId");
    }
    if (key === "VariantAnalysisTablePPMPlaceholder.adGroupName") {
      toRemove.push("VariantAnalysisTablePPMPlaceholder.adGroupId");
      if (!stillHasCampaign) {
        toRemove.push("VariantAnalysisTablePPMPlaceholder.campaignId");
        toRemove.push("VariantAnalysisTablePPMPlaceholder.campaignName");
      }
    }
    if (key === "VariantAnalysisTablePPMPlaceholder.portfolioName") {
      toRemove.push("VariantAnalysisTablePPMPlaceholder.portfolioId");
    }
    onExtraGroupByChange && onExtraGroupByChange(toRemove, "remove");
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (groupByOption) => {
    if (groupByOptionsData) {
      const dimensions = groupByOption.dimensions || [];
      onExtraGroupByChange && onExtraGroupByChange(dimensions, "add");
    }
    handleMenuClose();
  };

  const logo = {
    amazon: amazonLogo,
    myntra: myntraLogo,
    flipkart: flipkartLogo,
    adyogi: adyogiLogo,
  };
  const userPlatform =
    JSON.parse(localStorage.getItem("user_platform")) || "amazon";

  if (dataBody.length === 0 && !isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "250px", // adjust as needed
          color: "text.secondary",
          gap: 1.5,
        }}
      >
        <SearchOffIcon sx={{ fontSize: 48, color: "grey.500" }} />
        <Typography
          variant="subtitle1"
          sx={highlightNoDataFound ? { color: "#D32F2F" } : undefined}
        >
          {noDataFoundMessage}
        </Typography>
      </Box>
    );
  }

  const stickyLeftStyle = (left = 0, bgColor = "#ffffff") => ({
    position: "sticky",
    left: (left || 0) + leadingOffset,
    zIndex: 3,
    background: bgColor,
    backgroundClip: "padding-box",
    boxSizing: "border-box",
    borderRight: "1px solid #ddd",
    // Keeps the divider visible when adjacent sticky columns overlap on scroll
    boxShadow: "1px 0 0 0 #ddd",
    color: "#00000099",
    fontWeight: 600,
    fontSize: "0.75rem",
    whiteSpace: "nowrap",
  });

  // Lock sticky column width so stickyLeft offsets stay accurate and borders don't get covered.
  const stickyColumnWidthSx = (col) => {
    const colWidth = col?.minWidth || 120;
    return {
      minWidth: colWidth,
      width: colWidth,
      maxWidth: colWidth,
    };
  };

  const stickyBodyCellSx = (col, extras = {}) => {
    const isSticky = col.isSticky;
    return {
      position: isSticky ? "sticky" : "static",
      left: isSticky ? (col.stickyLeft || 0) + leadingOffset : "auto",
      zIndex: isSticky ? 2 : 1,
      ...stickyColumnWidthSx(col),
      ...(isSticky
        ? {
            backgroundClip: "padding-box",
            boxSizing: "border-box",
            boxShadow: "1px 0 0 0 #ddd",
          }
        : {}),
      borderRight: "1px solid #ddd",
      padding: "14px 16px",
      fontSize: "0.75rem",
      ...extras,
    };
  };

  // const stickyRightStyle = (right = 0, bgColor = "#ffffff") => ({
  //   position: "sticky",
  //   right,
  //   zIndex: 3,
  //   background: bgColor,
  //   borderLeft: "1px solid #ddd",
  //   fontWeight: 600,
  //   fontSize: "0.75rem",
  //   whiteSpace: "nowrap",
  //   textAlign: "center",
  //   color: "#00000099",
  // });

  const commonCellStyle = (isActive, bgColor = "#F7F6FE") => ({
    background: isActive ? bgColor : "#ffffff",
    fontWeight: 600,
    fontSize: "0.75rem",
    whiteSpace: "nowrap",
    padding: "6px 16px",
    borderRight: "1px solid #ddd",
    minWidth: 120,
  });

  const zIndexes = {
    header: 5,
    summary: 4,
    stickyColumn: 6,
  };

  // Row heights that drive sticky `top` offsets for header rows below (same pattern as TargetingV2).
  const TITLE_ROW_HEIGHT = 56;
  const COMPARISON_LABEL_ROW_HEIGHT = 40;
  const summaryRowTop =
    TITLE_ROW_HEIGHT + (isComparisonView ? COMPARISON_LABEL_ROW_HEIGHT : 0);

  const dimensionColumns = dataHeads.filter((col) => col?.isDimension);
  const firstDimensionColumnKey = dimensionColumns?.[0]?.key || "";
  const lastStickyDimensionColumnKey =
    [...dimensionColumns].reverse().find((col) => col.isSticky)?.key ||
    firstDimensionColumnKey;

  const leadingCellSx = (left, width, bgColor = "#fff", extra = {}) => ({
    ...stickyLeftStyle(0, bgColor),
    left,
    width,
    minWidth: width,
    maxWidth: width,
    padding: "8px 4px",
    ...extra,
  });

  const renderLeadingHeaderCells = (bgColor = "#fff", extra = {}) => (
    <>
      {showCheckbox ? (
        <TableCell sx={leadingCellSx(0, CHECKBOX_COL_WIDTH, bgColor, extra)}>
          <Tooltip
            title={selectionEnabled ? "" : SELECTION_DISABLED_TOOLTIP}
            arrow
            placement="top"
            disableHoverListener={selectionEnabled}
          >
            <span>
              <Checkbox
                size="small"
                checked={allSelectableChecked}
                indeterminate={someSelectableChecked && !allSelectableChecked}
                disabled={!selectionEnabled}
                onChange={(e) => onToggleAll?.(e.target.checked)}
                sx={{ p: 0.5 }}
              />
            </span>
          </Tooltip>
        </TableCell>
      ) : null}
      {showAdStatusSwitch ? (
        <TableCell
          sx={leadingCellSx(
            showCheckbox ? CHECKBOX_COL_WIDTH : 0,
            STATUS_COL_WIDTH,
            bgColor,
            extra
          )}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#00000099" }}
          >
            Ad Status
          </Typography>
        </TableCell>
      ) : null}
    </>
  );

  const renderLeadingPlaceholderCells = (
    bgColor = "#f9f9f9",
    extra = {},
    withSkeleton = false
  ) => (
    <>
      {showCheckbox ? (
        <TableCell sx={leadingCellSx(0, CHECKBOX_COL_WIDTH, bgColor, extra)}>
          {withSkeleton ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Skeleton variant="rectangular" width={16} height={16} />
            </Box>
          ) : null}
        </TableCell>
      ) : null}
      {showAdStatusSwitch ? (
        <TableCell
          sx={leadingCellSx(
            showCheckbox ? CHECKBOX_COL_WIDTH : 0,
            STATUS_COL_WIDTH,
            bgColor,
            extra
          )}
        >
          {withSkeleton ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Skeleton variant="rounded" width={28} height={16} />
            </Box>
          ) : null}
        </TableCell>
      ) : null}
    </>
  );

  const renderLeadingBodyCells = (row, rowIdx, bgColor = "white") => (
    <>
      {showCheckbox ? (
        <TableCell sx={leadingCellSx(0, CHECKBOX_COL_WIDTH, bgColor, { zIndex: 2 })}>
          <Tooltip
            title={selectionEnabled ? "" : SELECTION_DISABLED_TOOLTIP}
            arrow
            placement="top"
            disableHoverListener={selectionEnabled}
          >
            <span>
              <Checkbox
                size="small"
                checked={!!row?.isChecked}
                disabled={!selectionEnabled}
                onChange={() => onToggleRow?.(rowIdx)}
                sx={{ p: 0.5 }}
              />
            </span>
          </Tooltip>
        </TableCell>
      ) : null}
      {showAdStatusSwitch ? (
        <TableCell
          sx={leadingCellSx(
            showCheckbox ? CHECKBOX_COL_WIDTH : 0,
            STATUS_COL_WIDTH,
            bgColor,
            { zIndex: 2 }
          )}
        >
          <Tooltip
            title={adStatusSwitchEnabled ? "" : SELECTION_DISABLED_TOOLTIP}
            arrow
            placement="top"
            disableHoverListener={adStatusSwitchEnabled}
          >
            <span>
              <SwitchBtn
                checked={isAdStatusEnabled(row)}
                disabled={!adStatusSwitchEnabled}
                onChange={(event) => {
                  event?.stopPropagation?.();
                  onToggleStatus?.(row);
                }}
              />
            </span>
          </Tooltip>
        </TableCell>
      ) : null}
    </>
  );

  const HEADER_MAX_LENGTH = 22;
  const getTruncatedHeaderText = (headerText = "") => {
    const normalizedText = String(headerText);
    if (normalizedText.length <= HEADER_MAX_LENGTH) return normalizedText;
    return `${normalizedText.slice(0, HEADER_MAX_LENGTH)}...`;
  };

  const SummaryCellContent = ({
    col,
    summaryData,
    summaryDataWithoutFilter,
    numberFormatter,
    showPercentage,
    comparisonChangePercent,
  }) => {
    const value = summaryData?.[col.key];
    const totalValue = summaryDataWithoutFilter?.[col.key];

    if (value === undefined || value === null) return "-";

    const formattedValue = numberFormatter(
      value,
      !!col.isCurrencyFormat,
      col?.format,
      col?.maximumFractionDigits
    );

    const formattedTotalValue = numberFormatter(
      totalValue,
      !!col.isCurrencyFormat,
      col?.format,
      col?.maximumFractionDigits
    );

    const percentage =
      typeof totalValue === "number" && totalValue !== 0 && !col?.isDerivedValue
        ? (value / totalValue) * 100
        : null;

    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems={col.alignment === "left" ? "flex-start" : "flex-end"}
        gap={0.5}
        sx={{ whiteSpace: "nowrap" }}
      >
        {/* Total Summary (visible when showPercentage is ON) */}
        {showPercentage && percentage && (
          <Tooltip
            title={`Total summary: ${formattedTotalValue}`}
            arrow
            placement="top"
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.75rem",
                color: "#626262",
                lineHeight: 1.1,
                cursor: "default",
                fontWeight: 400,
              }}
            >
              {`${formattedTotalValue} (100%)`}
            </Typography>
          </Tooltip>
        )}

        {/* Filtered Summary (always visible) */}
        <Tooltip
          title={
            showPercentage && percentage !== null && !isNaN(percentage)
              ? `Filtered value: ${formattedValue} (${percentage?.toFixed(
                  2
                )}% of total)`
              : `Filtered value: ${formattedValue}`
          }
          arrow
          placement="top"
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              lineHeight: 1.1,
              cursor: "default",
            }}
          >
            {formattedValue}{" "}
            {showPercentage &&
              percentage !== null &&
              !isNaN(percentage) &&
              percentage !== 100 && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "#626262",
                  }}
                >
                  ({percentage?.toFixed(0)}%)
                </Typography>
              )}
          </Typography>
        </Tooltip>

        {/* comparison percentage */}
        {isComparisonView && comparisonChangePercent ? (
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.75rem",
              color: comparisonChangePercent?.[col.key] < 0 ? "#d32f2f" : "#388e3c",
              lineHeight: 1.1,
              cursor: "default",
            }}
          >
            {comparisonChangePercent?.[col.key] ? `(${comparisonChangePercent?.[col.key]?.toFixed(0)}%)` : null}
          </Typography>
        ): null}
      </Box>
    );
  };

  const getFormattedCompChangePercent = (value) => {
    // default structure
    const formattedObj = {
      printableValue: "-",
      numericValue: value,
    };
    if (value === undefined || value === null || isNaN(value)) {
      return formattedObj;
    }
    if (value === 2147483647 || value === -2147483648) {
      formattedObj.printableValue = "∞";
      return formattedObj;
    }
    formattedObj.printableValue = `(${Number(value).toFixed(0)}%)`;
    return formattedObj;
  };


  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "auto",
        border: "1px solid #ddd",
        borderRadius: "8px",
        boxShadow: "none",
        marginTop: 2,
      }}
    >
      <TableContainer sx={{ maxHeight: 600, minHeight: 400 }}>
        <Table stickyHeader size="small" sx={{ borderCollapse: "separate" }}>
          <TableHead>
            {/* Header Row */}
            <TableRow
              sx={{
                zIndex: zIndexes.header,
                position: "sticky",
                top: 0,
                height: TITLE_ROW_HEIGHT,
              }}
            >
              {renderLeadingHeaderCells("#fff", {
                top: 0,
                borderBottom: "none",
                zIndex: zIndexes.stickyColumn,
              })}
              {dataHeads.map((col, colIndex) =>
                col.isDimension ? (
                  <TableCell
                    key={`${col.key}-header-sticky`}
                    sx={{
                      ...(col.isSticky
                        ? stickyLeftStyle(
                            col.stickyLeft,
                            orderBy === col.key ? "#F7F6FE" : "#fff"
                          )
                        : {
                            background:
                              orderBy === col.key ? "#F7F6FE" : "#fff",
                            borderRight: "1px solid #ddd",
                            color: "#00000099",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            textAlign: "left",
                          }),
                      ...stickyColumnWidthSx(col),
                      padding: "14px 16px",
                      zIndex: col.isSticky
                        ? zIndexes.stickyColumn
                        : zIndexes.header,
                      textAlign: "left",
                      borderBottom: "none",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort(col.key)}
                  >
                    <TableSortLabel
                      active={orderBy === col.key}
                      direction={orderBy === col.key ? order : "asc"}
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          color: "#00000099 !important",
                        },
                      }}
                    >
                      <Tooltip
                        title={col.tooltip || col.shortTitle || ""}
                        arrow
                        placement="top"
                        disableHoverListener={
                          !col.tooltip &&
                          String(col.shortTitle || "").length <= HEADER_MAX_LENGTH
                        }
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: "#00000099",
                          }}
                        >
                          {getTruncatedHeaderText(col.shortTitle)}
                        </Typography>
                      </Tooltip>
                    </TableSortLabel>
                    {/* add plus icon to add extra column */}
                   {col.key === lastStickyDimensionColumnKey && showGroupByPlusIcon && ( <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlusClick(e);
                          }}
                          sx={{
                            position: "absolute",
                            right: -12,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 24,
                            height: 24,
                            backgroundColor: "#7558ea",
                            color: "white",
                            zIndex: zIndexes.stickyColumn + 2,
                            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                            "&:hover": {
                              backgroundColor: "#5a42c7",
                            },
                            padding: 0,
                          }}
                        >
                          <AddIcon sx={{ fontSize: 16 }} />
                        </IconButton>)}

                          {/* remove icon to remove extra column */}
                         {col.key !== firstDimensionColumnKey && ( <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveExtraColumnClick(col.key);
                          }}
                          sx={{
                            position: "absolute",
                            right: 10,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 24,
                            height: 24,
                            zIndex: zIndexes.stickyColumn + 2,
                            padding: 0,
                          }}
                        >
                          <CancelIcon sx={{ fontSize: 16 }} />
                        </IconButton>)}
                  </TableCell>
                ) : (
                  <TableCell
                    key={`${col.key}-header`}
                    align={col.alignment ? col.alignment : "right"}
                    sx={{
                      ...commonCellStyle(orderBy === col.key),
                      borderBottom: "none",
                      zIndex: zIndexes.header,
                      minWidth: col.minWidth || 120,
                    }}
                    colSpan={isComparisonView && col.colSpan ? col.colSpan : 1}
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={1}
                      alignItems={col.alignment ? col.alignment : "flex-end"}
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleSort(col.key)}
                    >
                      <TableSortLabel
                        active={orderBy === col.key}
                        direction={orderBy === col.key ? order : "asc"}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          width: "100%",
                          flexDirection: "row-reverse",
                          justifyContent: "space-between",
                        }}
                      >
                        <img
                          src={logo[userPlatform] || adyogiLogo}
                          alt="platform"
                          width={14}
                          height={14}
                          style={{ display: "block" }}
                        />
                        {orderBy === col.key && (
                          <Box sx={visuallyHidden}>
                            {order === "desc"
                              ? "sorted descending"
                              : "sorted ascending"}
                          </Box>
                        )}
                      </TableSortLabel>
                      <Tooltip
                        title={col.tooltip || col.shortTitle || ""}
                        arrow
                        placement="top"
                        disableHoverListener={
                          !col.tooltip &&
                          String(col.shortTitle || "").length <= HEADER_MAX_LENGTH
                        }
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: "#00000099",
                            textAlign: col.isHeadRightAlign ? "right" : "left",
                          }}
                        >
                          {getTruncatedHeaderText(col.shortTitle)}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </TableCell>
                )
              )}
            </TableRow>
            {/* Comparison view label Row */}
            {isComparisonView && (
              <TableRow
                sx={{
                  position: "sticky",
                  top: TITLE_ROW_HEIGHT,
                  zIndex: zIndexes.summary,
                  backgroundColor: "#f9f9f9",
                  height: COMPARISON_LABEL_ROW_HEIGHT,
                }}
              >
                {renderLeadingPlaceholderCells("#f9f9f9", {
                  top: TITLE_ROW_HEIGHT,
                  borderTop: "1px solid #ddd",
                  zIndex: zIndexes.stickyColumn,
                })}
                {dataHeads.map((col) =>
                  col.isDimension ? (
                    <TableCell
                      key={`summary-single-value-${col.key}`}
                      sx={{
                        ...(col.isSticky
                          ? stickyLeftStyle(
                              col.stickyLeft,
                              orderBy === col.key ? "#EAE6FD" : "#f9f9f9"
                            )
                          : {
                              background:
                                orderBy === col.key ? "#EAE6FD" : "#f9f9f9",
                              borderRight: "1px solid #ddd",
                            }),
                        ...stickyColumnWidthSx(col),
                        position: "sticky",
                        top: TITLE_ROW_HEIGHT,
                        padding: "8px 16px",
                        zIndex: col.isSticky
                          ? zIndexes.stickyColumn
                          : zIndexes.summary,
                        borderTop: "1px solid #ddd",
                        fontWeight: 600,
                      }}
                    >
                      -
                    </TableCell>
                  ) : (
                    <>
                      <TableCell
                        key={`summary-current-${col.key}`}
                        align={col.alignment ? col.alignment : "right"}
                        sx={{
                          fontSize: "0.75rem",
                          padding: "8px 16px",
                          background:
                            orderBy === col.key ? "#EAE6FD" : "#f9f9f9",
                          borderRight: "1px solid #ddd",
                          minWidth: col.minWidth || 120,
                          fontWeight: 600,
                          position: "sticky",
                          top: TITLE_ROW_HEIGHT,
                          zIndex: zIndexes.summary,
                          borderTop: "1px solid #ddd",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {col.colSpan === 2 ? "Current" : "-"}
                      </TableCell>
                      {col.colSpan === 2 && (
                        <TableCell
                          key={`summary-previous-${col.key}`}
                          align={col.alignment ? col.alignment : "right"}
                          sx={{
                            fontSize: "0.75rem",
                            padding: "8px 16px",
                            borderRight: "1px solid #ddd",
                            minWidth: col.minWidth || 120,
                            fontWeight: 600,
                            position: "sticky",
                            top: TITLE_ROW_HEIGHT,
                            zIndex: zIndexes.summary,
                            borderTop: "1px solid #ddd",
                            whiteSpace: "nowrap",
                            color: "#00000099",
                            background: "#f9f9f9",
                          }}
                        >
                          Previous
                        </TableCell>
                      )}
                    </>
                  )
                )}
              </TableRow>
            )}
            {/* Summary Row */}
            <TableRow
              sx={{
                position: "sticky",
                top: summaryRowTop,
                zIndex: zIndexes.summary,
                backgroundColor: "#f9f9f9",
              }}
            >
              {renderLeadingPlaceholderCells("#f9f9f9", {
                top: summaryRowTop,
                borderTop: "1px solid #ddd",
                zIndex: zIndexes.stickyColumn,
              })}
              {dataHeads.map((col, colIndex) =>
                col.isDimension ? (
                  <TableCell
                    key={`summary-${col.key}`}
                    sx={{
                      ...(col.isSticky
                        ? stickyLeftStyle(
                            col.stickyLeft,
                            orderBy === col.key ? "#EAE6FD" : "#f9f9f9"
                          )
                        : {
                            background:
                              orderBy === col.key ? "#EAE6FD" : "#f9f9f9",
                            borderRight: "1px solid #ddd",
                          }),
                      ...stickyColumnWidthSx(col),
                      position: "sticky",
                      top: summaryRowTop,
                      padding: "14px 16px",
                      zIndex: col.isSticky
                        ? zIndexes.stickyColumn
                        : zIndexes.summary,
                      borderTop: "1px solid #ddd",
                      fontWeight: 600,
                    }}
                  >
                    {col.key === firstDimensionColumnKey ? "Summary" : ""}
                  </TableCell>
                ) : (
                  <>
                    <TableCell
                      key={`summary-${col.key}`}
                      align={col.alignment ? col.alignment : "right"}
                      sx={{
                        fontSize: "0.75rem",
                        padding: "14px 16px",
                        background: orderBy === col.key ? "#EAE6FD" : "#f9f9f9",
                        borderRight: "1px solid #ddd",
                        minWidth: col.minWidth || 120,
                        fontWeight: 600,
                        position: "sticky",
                        top: summaryRowTop,
                        zIndex: zIndexes.summary,
                        borderTop: "1px solid #ddd",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {isLoading ? (
                        "-"
                      ) : (
                        <SummaryCellContent
                          col={col}
                          summaryData={
                            isComparisonView && col.colSpan === 2
                              ? summaryData?.current_data || {}
                              : summaryData || {}
                          }
                          comparisonChangePercent={isComparisonView && col.colSpan === 2 ? summaryData?.change_percentage_data || {} : null}
                          summaryDataWithoutFilter={summaryDataWithoutFilter}
                          numberFormatter={numberFormatter}
                          showPercentage={showPercentage}
                        />
                      )}
                    </TableCell>
                    {isComparisonView && col.colSpan === 2 && (
                      <TableCell
                        key={`summary-previous-${col.key}`}
                        align={col.alignment ? col.alignment : "right"}
                        sx={{
                          fontSize: "0.75rem",
                          padding: "14px 16px",
                          background: "#f9f9f9",
                          borderRight: "1px solid #ddd",
                          minWidth: col.minWidth || 120,
                          fontWeight: 600,
                          position: "sticky",
                          top: summaryRowTop,
                          zIndex: zIndexes.summary,
                          borderTop: "1px solid #ddd",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {isLoading ? (
                          "-"
                        ) : (
                          <SummaryCellContent
                            col={col}
                            summaryData={summaryData?.comparison_data || {}}
                            summaryDataWithoutFilter={summaryDataWithoutFilter}
                            numberFormatter={numberFormatter}
                            showPercentage={showPercentage}
                          />
                        )}
                      </TableCell>
                    )}
                  </>
                )
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading
              ? [...Array(10)].map((_, idx) => (
                  <TableRow key={`skeleton-${idx}`}>
                    {renderLeadingPlaceholderCells("white", { zIndex: 2 }, true)}
                    {dataHeads.map((col, colIdx) => {
                      return (
                       <>
                        <TableCell
                          key={`skeleton-${idx}-${col.key}-${colIdx}`}
                          align={col.alignment ? col.alignment : "right"}
                          sx={{
                            ...stickyBodyCellSx(col, { background: "white" }),
                          }}
                        >
                          {/* Handle special case for product image + name */}
                          {col.key ===
                          "VariantAnalysisTablePPMPlaceholder.asin" ? (
                            <Skeleton variant="text" width="60%" height={20} />
                          ) : (
                            <Skeleton
                              variant={col.skeletonVariant || "text"}
                              width="80%"
                              height={20}
                            />
                          )}
                        </TableCell>
                        {isComparisonView && col.colSpan === 2 && ( <TableCell
                          key={`skeleton-${idx}-${col.key}`}
                          align={col.alignment ? col.alignment : "right"}
                          sx={{
                            ...stickyBodyCellSx(col, { background: "white" }),
                          }}
                        >
                          {/* Handle special case for product image + name */}
                          {col.key ===
                          "VariantAnalysisTablePPMPlaceholder.asin" ? (
                            <Skeleton variant="text" width="60%" height={20} />
                          ) : (
                            <Skeleton
                              variant={col.skeletonVariant || "text"}
                              width="80%"
                              height={20}
                            />
                          )}
                        </TableCell>)}
                       </>
                      );
                    })}
                  </TableRow>
                ))
              : dataBody.map((row, rowIdx) => (
                  <TableRow
                    key={
                      (getRowKey && getRowKey(row, rowIdx)) ||
                      row["VariantAnalysisTablePPMPlaceholder.asin"] ||
                      rowIdx
                    }
                    hover
                    sx={{
                      "&:hover td": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    {renderLeadingBodyCells(row, rowIdx)}
                    {dataHeads.map((col, colIdx) => {
                      const isSticky = col.isSticky;
                      const value =
                        isComparisonView && col.colSpan === 2
                          ? row?.current_data?.[col.key]
                          : row?.[col.key];
                      const previousValue =
                        isComparisonView && col.colSpan === 2
                          ? row?.comparison_data?.[col.key]
                          : null;

                      const comparisonChangePercent =
                        isComparisonView && col.colSpan === 2
                          ? getFormattedCompChangePercent(row?.change_percentage_data?.[col.key]) : null;
                      const {
                        image: imageKey,
                        productName,
                        asin,
                      } = col?.innerKeys || {};
                      const asinKey = asin?.value;
                      const asinLabel = asin?.label || "ASIN";
                      let imageValue = "";
                      let productNameValue = "";
                      if (userPlatform === "amazon") {
                        imageValue = row[imageKey] || "";
                        productNameValue =
                          row[productName] || "";
                      } else {
                        imageValue = row[imageKey] || "";
                        productNameValue = row[productName] || "";
                      }
                      const asinValue = row[asinKey] || "";

                      return (
                        <>
                          <TableCell
                            key={`${row["VariantAnalysisTablePPMPlaceholder.asin"]}-${col.key}`}
                            align={col.alignment ? col.alignment : "right"}
                            sx={{
                              ...stickyBodyCellSx(col, {
                                position: isSticky ? "sticky" : "unset",
                                background:
                                  orderBy === col.key ? "#F7F6FE" : "white",
                              }),
                              ...(NAME_WRAP_COLUMN_KEYS.has(col.key)
                                ? nameWrapCellSx
                                : {}),
                            }}
                          >
                            {/* Special rendering for product with image */}
                            {[
                              "VariantAnalysisTablePPMPlaceholder.asin",
                              "product_id","campaign_id","ad_group_id",
                            ].includes(col.key) ? (
                              <Tooltip title={productNameValue || ""} arrow>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  {/* Left: Image */}
                                  {!!imageKey && (
                                    <img
                                      src={
                                        imageValue && imageValue.trim() !== ""
                                          ? imageValue
                                          : imageThumbnail
                                      }
                                      alt="product"
                                      width={36}
                                      height={36}
                                      style={{ borderRadius: 4 }}
                                    />
                                  )}

                                  {/* Right: Product name + ASIN */}
                                  <Stack direction="column" spacing={0}>
                                    {/* Product Name (2 lines max with ellipsis) */}
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.75rem",
                                        display: "-webkit-box",
                                        WebkitLineClamp: 2, // limit to 2 lines
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: 170,
                                        lineHeight: 1.3,
                                      }}
                                    >
                                      {productNameValue}
                                    </Typography>

                                    {/* ASIN */}
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: "0.65rem",
                                        color: "grey.600",
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {asinLabel}: {asinValue || ""}
                                    </Typography>
                                  </Stack>
                                </Stack>
                              </Tooltip>
                            ) : value !== undefined &&
                              value !== null &&
                              value !== "" ? (
                              (() => {
                                const formattedValue = numberFormatter(
                                  value,
                                  !!col.isCurrencyFormat,
                                  col?.format,
                                  col?.maximumFractionDigits
                                );

                                // calculate percentage if summary value exists and is numeric
                                const summaryValue = summaryData?.[col.key];
                                const formattedSummaryValue = numberFormatter(
                                  summaryValue,
                                  !!col.isCurrencyFormat,
                                  col?.format,
                                  col?.maximumFractionDigits
                                );
                                const percentage =
                                  typeof summaryValue === "number" &&
                                  !col?.isDerivedValue &&
                                  summaryValue !== 0
                                    ? (value / summaryValue) * 100
                                    : null;

                                return (
                                  <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems={
                                      col.alignment === "left"
                                        ? "flex-start"
                                        : "flex-end"
                                    }
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.75rem",
                                        fontWeight: 500,
                                        ...(NAME_WRAP_COLUMN_KEYS.has(col.key)
                                          ? nameWrapTextSx
                                          : {}),
                                      }}
                                    >
                                      {formattedValue}
                                    </Typography>
                                    {isComparisonView && comparisonChangePercent?.printableValue && ( 
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontSize: "0.75rem",
                                              color: comparisonChangePercent.numericValue < 0 ? "#d32f2f" : "#388e3c",
                                              lineHeight: 1.1,
                                              cursor: "default",
                                            }}
                                          >
                                            {comparisonChangePercent.printableValue}
                                          </Typography>
                                      )}
                                    {showPercentage &&
                                      percentage !== null &&
                                      !isNaN(percentage) && (
                                        <Tooltip
                                          title={`This contributes ${percentage?.toFixed(
                                            2
                                          )}% of the filtered summary ${formattedSummaryValue}`}
                                          arrow
                                          placement="top"
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontSize: "0.75rem",
                                              color: "#626262",
                                              lineHeight: 1.1,
                                              cursor: "default",
                                            }}
                                          >
                                            ({percentage?.toFixed(0)}%)
                                          </Typography>
                                        </Tooltip>
                                      )}
                                  </Box>
                                );
                              })()
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          {/* previous value */}
                          {isComparisonView && col.colSpan === 2 && (
                            <TableCell
                              key={`${row["VariantAnalysisTablePPMPlaceholder.asin"]}-${col.key}-previous`}
                              align={col.alignment ? col.alignment : "right"}
                              sx={{
                                ...stickyBodyCellSx(col, {
                                  position: isSticky ? "sticky" : "unset",
                                  background: "white",
                                }),
                                ...(NAME_WRAP_COLUMN_KEYS.has(col.key)
                                  ? nameWrapCellSx
                                  : {}),
                              }}
                            >
                              {/* Special rendering for product with image */}
                              {previousValue !== undefined &&
                              previousValue !== null &&
                              previousValue !== ""
                                ? (() => {
                                    const formattedPreviousValue =
                                      numberFormatter(
                                        previousValue,
                                        !!col.isCurrencyFormat,
                                        col?.format,
                                        col?.maximumFractionDigits
                                      );
                                    return (
                                      <Box
                                        display="flex"
                                        flexDirection="column"
                                        alignItems={
                                          col.alignment === "left"
                                            ? "flex-start"
                                            : "flex-end"
                                        }
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontSize: "0.75rem",
                                            fontWeight: 500,
                                            ...(NAME_WRAP_COLUMN_KEYS.has(
                                              col.key
                                            )
                                              ? nameWrapTextSx
                                              : {}),
                                          }}
                                        >
                                          {formattedPreviousValue}
                                        </Typography>
                                      </Box>
                                    );
                                  })()
                                : "-"}
                            </TableCell>
                          )}
                        </>
                      );
                    })}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
            // minWidth: 150,
            maxWidth:200,
            maxHeight:400,
            mt: 1,
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {groupByOptionsData?.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleMenuItemClick(option)}
            sx={{
              fontSize: "12px",
              textWrap: "wrap",
              wordBreak: "break-word",
              padding: "8px 16px",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
      {/* 
      <CustomPagination
        currentPage={page}
        totalPages={Math.ceil(dataBody.length / rowsPerPage)}
        onPageChange={handleChangePage}
        totalItems={dataBody.length}
        itemsPerPage={rowsPerPage}
      /> */}
    </Paper>
  );
}

export default memo(ProductAnalyticsTable);
